import { Navigation } from '../home/components/Navigation';
import { Footer } from '../home/components/Footer';
import { useEffect } from 'react';
import { SEO } from '../../components/SEO';

export default function TermsOfServicePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Terms of Service | AI Receptionist Service"
        description="Review our terms of service to understand the rules and guidelines for using our AI receptionist and phone answering services."
        canonical="/terms-of-service"
        schemas={[
          {
            type: 'WebPage',
            data: {
              '@type': 'WebPage',
              name: 'Terms of Service',
              description: 'Terms of service for AI Receptionist Service',
              url: `${siteUrl}/terms-of-service`,
              publisher: {
                '@type': 'Organization',
                name: 'AI Receptionist Service',
                url: siteUrl
              }
            }
          }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />
        
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-4">
              Dino Nuggets, LLC Terms of Service
            </h1>
            <p className="text-gray-600 mb-12">
              Last Updated: January 2025
            </p>

            <div className="prose prose-lg max-w-none">
              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  1. Acceptance of Terms
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  By accessing and using Dino Nuggets services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Service, please do not use our services.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  2. Description of Services
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Dino Nuggets provides technology solutions for small businesses, including but not limited to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Website design and hosting services</li>
                  <li>Voice AI assistant technology</li>
                  <li>Conversational AI solutions</li>
                  <li>Automated scheduling and calendar management</li>
                  <li>Marketing automation and social media tools</li>
                  <li>QR code form solutions</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  3. Dino Nuggets SMS Messaging Program
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By opting in to receive SMS messages from Dino Nuggets, you agree to receive text messages for the following purposes:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Appointment confirmations and reminders</li>
                  <li>Service updates and notifications</li>
                  <li>Promotional offers and marketing messages</li>
                  <li>Customer support and service-related communications</li>
                  <li>Account alerts and important announcements</li>
                  <li>Survey and feedback requests</li>
                </ul>

                <p className="text-gray-700 leading-relaxed mt-6 mb-4">
                  Message frequency may vary.
                </p>
                
                <p className="text-gray-700 leading-relaxed mt-6 mb-4">
                  <strong>Opt-Out:</strong> You can cancel the SMS service at any time. Simply text STOP to any message you receive from us. After sending “STOP,” we will confirm your unsubscribe status via SMS. After this confirmation, you will no longer receive SMS messages from us.
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  To rejoin, sign up again as you did initially.
                </p>

                <p className="text-gray-700 leading-relaxed mt-6 mb-4">
                  <strong>Help:</strong> If you experience issues with the messaging program, reply HELP for assistance or contact us directly at:
                </p>
                <ul className="pl-6 text-gray-700 space-y-2">
                  <li>support@dino-nuggets.com</li>
                  <li>(586) 789-9699</li>

                </ul>

                <p className="text-gray-700 leading-relaxed mt-6 mb-4">
                  <strong>Message and Data Rate Disclosure:</strong> Message and data rates may apply for any messages sent to or from us. The number of messages you receive will vary depending on your interaction with our services. Please contact your mobile carrier for details about your messaging plan and any applicable charges.
                </p>
                
                <p className="text-gray-700 leading-relaxed mt-6 mb-4">
                  <strong>Carrier Liability Disclaimer:</strong> Carriers are not liable for delayed or undelivered messages. SMS delivery is subject to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Carrier network availability and coverage</li>
                  <li>Mobile device compatibility and settings</li>
                  <li>Technical limitations beyond our control</li>
                  <li>Carrier filtering or blocking policies</li>
                </ul>

                <p className="text-gray-700 leading-relaxed mt-6">
                  <strong>Age Restriction:</strong> You must be at least 18 years of age to opt in to receive SMS messages from Dino Nuggets. By providing your mobile phone number and opting in, you confirm that you are 18 years of age or older and have the authority to agree to receive text messages at the number provided.
                </p>

                <p className="text-gray-700 leading-relaxed mt-6">
                <strong>Privacy:</strong> For privacy-related information, please review our Privacy Policy at https://dino-nuggets.com/privacy-policy
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  4. User Obligations
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  As a user of our services, you agree to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorized use of your account</li>
                  <li>Use our services only for lawful purposes</li>
                  <li>Not interfere with or disrupt our services or servers</li>
                  <li>Not attempt to gain unauthorized access to any portion of our services</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  5. Payment Terms
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Monthly Subscription:</strong> The Dino Nuggets Package is billed at $350 per month. Payment is due at the beginning of each billing cycle.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Add-On Services:</strong> Additional services such as Facebook Ads, Google Ads, and Online Store Setup are subject to separate fees as outlined in our pricing.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Refund Policy:</strong> Monthly subscription fees are non-refundable. One-time setup fees are non-refundable once work has commenced.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Late Payments:</strong> Failure to pay on time may result in suspension or termination of services.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  6. Cancellation and Termination
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>By You:</strong> You may cancel your subscription at any time by providing 30 days written notice. You will remain responsible for all charges incurred through the end of your billing cycle.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>By Us:</strong> We reserve the right to suspend or terminate your access to our services at any time for violation of these Terms of Service or for any other reason at our sole discretion.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  7. Intellectual Property
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All content, features, and functionality of our services, including but not limited to text, graphics, logos, icons, images, audio clips, and software, are the exclusive property of Dino Nuggets and are protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You retain ownership of any content you provide to us. By using our services, you grant us a license to use, reproduce, and display your content solely for the purpose of providing our services to you.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  8. Limitation of Liability
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  To the maximum extent permitted by law, Dino Nuggets shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of our services.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  9. Warranty Disclaimer
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Our services are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that our services will be uninterrupted, secure, or error-free.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  10. Indemnification
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless Dino Nuggets and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising out of or in any way connected with your use of our services or violation of these Terms of Service.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  11. Governing Law
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  These Terms of Service shall be governed by and construed in accordance with the laws of the jurisdiction in which Dino Nuggets operates, without regard to its conflict of law provisions.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  12. Changes to Terms
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify these Terms of Service at any time. We will notify you of any changes by posting the new Terms of Service on this page and updating the "Last Updated" date. Your continued use of our services after such modifications constitutes your acceptance of the updated terms.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  13. Contact Information
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  If you have any questions about these Terms of Service, please contact us at:
                </p>
                <div className="mt-4 p-6 bg-gray-50 rounded-lg">
                  <p className="text-gray-700"><strong>Dino Nuggets, LLC</strong></p>
                  <p className="text-gray-700">Email: support@Dino-Nuggets.com</p>
                  <p className="text-gray-700">Phone: (586) 789-9699</p>
                </div>
              </section>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
